/**
 * PhoneInput — country-code dropdown + local number input.
 *
 * Behaviour:
 *   - On first mount, auto-detects the country from the browser locale,
 *     falling back to Qatar (+974).
 *   - Emits an E.164-ish string ("+97412345678") AND the ISO country
 *     code via `onChange(phone, iso)` so callers can store both.
 *   - Backwards-compat: callers can pass an existing `{ value, country }`
 *     pair and we'll parse the dial code out.
 *
 * Props:
 *   value          — current phone (e.g. "+97412345678") or ""
 *   country        — ISO code (e.g. "QA") or null
 *   onChange(phone, iso) — required
 *   required       — boolean; if true, attributes propagate to the input
 *   testId         — root data-testid
 *   placeholder    — local number placeholder
 *   disabled
 */
import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { COUNTRIES, detectCountry, lookupCountry } from '@/lib/countries';

function parsePhone(value, defaultCountry) {
  if (!value || typeof value !== 'string') {
    return { iso: defaultCountry.iso, dial: defaultCountry.dial, local: '' };
  }
  const trimmed = value.trim();
  // Try matching the longest dial code first
  const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    if (trimmed.startsWith(c.dial)) {
      return { iso: c.iso, dial: c.dial, local: trimmed.slice(c.dial.length).trim() };
    }
  }
  // Fallback — treat the whole thing as the local number
  return { iso: defaultCountry.iso, dial: defaultCountry.dial, local: trimmed };
}

export default function PhoneInput({
  value, country,
  onChange,
  required = false,
  testId = 'phone-input',
  placeholder = '12 345 6789',
  disabled = false,
}) {
  // Memo: detect default country exactly once per mount (lazy init via useState).
  const [defaultCountry] = useState(() => (country && lookupCountry(country)) || detectCountry());

  const [selected, setSelected] = useState(defaultCountry);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [local, setLocal] = useState('');
  const dropdownRef = useRef(null);

  // Initialise from props on mount AND whenever parent-driven value changes
  // from "" to a populated string (e.g. autofill from LinkedIn).
  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on prop-sync */
  useEffect(() => {
    const parsed = parsePhone(value, defaultCountry);
    const c = lookupCountry(country) || lookupCountry(parsed.iso) || defaultCountry;
    setSelected(c);
    setLocal(parsed.local);
  }, [value, country, defaultCountry]);
  /* eslint-enable */

  // Close dropdown on outside click
  useEffect(() => {
    const fn = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  const emit = (nextSelected, nextLocal) => {
    const cleanLocal = nextLocal.replace(/[^\d]/g, '');
    const composed = cleanLocal ? `${nextSelected.dial}${cleanLocal}` : '';
    onChange(composed, nextSelected.iso);
  };

  const pickCountry = (c) => {
    setSelected(c);
    setOpen(false);
    setQuery('');
    emit(c, local);
  };

  const filtered = query.trim()
    ? COUNTRIES.filter((c) => {
        const q = query.toLowerCase();
        return (
          c.name.toLowerCase().includes(q) ||
          c.dial.includes(q) ||
          c.iso.toLowerCase().includes(q)
        );
      })
    : COUNTRIES;

  return (
    <div className="relative" data-testid={testId} ref={dropdownRef}>
      <div className="flex items-stretch rounded-md border border-slate-200 focus-within:border-[#8B3FC5] overflow-hidden bg-white">
        {/* Country picker */}
        <button
          type="button"
          onClick={() => !disabled && setOpen((x) => !x)}
          disabled={disabled}
          data-testid={`${testId}-country`}
          className="flex items-center gap-1 px-2.5 bg-slate-50 hover:bg-slate-100 border-r border-slate-200 text-sm font-bold transition-colors disabled:opacity-50"
        >
          <span className="text-lg leading-none">{selected.flag}</span>
          <span className="text-xs font-mono">{selected.dial}</span>
          <ChevronDown size={11} className="text-slate-400"/>
        </button>
        {/* Number input */}
        <input
          type="tel"
          inputMode="tel"
          value={local}
          onChange={(e) => {
            const v = e.target.value;
            setLocal(v);
            emit(selected, v);
          }}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          data-testid={`${testId}-number`}
          className="flex-1 px-3 py-2 text-sm focus:outline-none disabled:bg-slate-50"
        />
      </div>

      {/* Dropdown panel */}
      {open && (
        <div
          data-testid={`${testId}-dropdown`}
          className="absolute z-50 mt-1 w-72 bg-white rounded-md shadow-lg border border-slate-200 max-h-80 overflow-hidden flex flex-col"
        >
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or dial code…"
                data-testid={`${testId}-search`}
                className="w-full pl-7 pr-2 py-1.5 text-xs rounded border border-slate-200 focus:outline-none focus:border-[#8B3FC5]"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-xs text-slate-400 text-center">No countries match.</div>
            )}
            {filtered.map((c) => {
              const active = c.iso === selected.iso;
              return (
                <button
                  key={c.iso}
                  type="button"
                  onClick={() => pickCountry(c)}
                  data-testid={`${testId}-option-${c.iso}`}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-[#FFF8E5] transition-colors text-left ${
                    active ? 'bg-[#FFF8E5] font-bold' : ''
                  }`}
                >
                  <span className="text-base leading-none">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="font-mono text-slate-500">{c.dial}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
